import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import SignaturePad from "../components/SignaturePad";
import { Logo } from "../lib/brand";
import { toast, Toaster } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicSignPage() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/sign/${token}`)
      .then((r) => setDoc(r.data))
      .catch((err) => setError(err?.response?.data?.detail || "Link unavailable"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your full name");
    if (!signature) return toast.error("Please add your signature");
    setSubmitting(true);
    try {
      await axios.post(`${API}/sign/${token}`, {
        image_base64: signature,
        full_name: name,
        email: email || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#2A0F5A]" data-testid="public-sign-page">
      <Toaster position="top-right" toastOptions={{ className: "rounded-none border border-[#E8E3F0] bg-white text-[#2A0F5A]" }} />
      <header className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={28} />
            <span className="font-serif text-2xl">Marianne</span>
            <span className="label-eyebrow ml-2 hidden sm:inline">Sign &amp; Send</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#4A5568]">
            <ShieldCheck size={14} className="text-[#7B56C9]" /> Encrypted signing link
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#4A5568]"><Loader2 size={14} className="animate-spin" /> Loading document…</div>
        ) : error ? (
          <div className="border border-[#E8E3F0] bg-white p-12 text-center">
            <div className="font-serif text-3xl mb-2">Link unavailable</div>
            <div className="text-sm text-[#4A5568]">{error}</div>
          </div>
        ) : done || doc.already_signed ? (
          <div className="border border-[#E8E3F0] bg-white p-12 text-center">
            <CheckCircle2 size={36} className="mx-auto text-[#7B56C9] mb-4" strokeWidth={1.4} />
            <div className="label-eyebrow text-[#C5A059] mb-2">Document signed</div>
            <h1 className="font-serif text-4xl tracking-tight mb-3">Thank you, {name || "signer"}.</h1>
            <p className="text-[#4A5568] max-w-md mx-auto">A signed copy will be retained by {doc.owner_name || "the document owner"}. You may close this window.</p>
          </div>
        ) : (
          <>
            <div className="label-eyebrow text-[#C5A059] mb-3">{doc.agreement_type || "Agreement"} · {doc.status}</div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight font-medium mb-2">{doc.title}</h1>
            <p className="text-sm text-[#4A5568] mb-8">Sent for signature by <strong className="text-[#2A0F5A]">{doc.owner_name}</strong></p>

            <div className="grid lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3 border border-[#E8E3F0] bg-white p-8 max-h-[70vh] overflow-auto">
                <div className="prose prose-slate max-w-none font-serif text-[15px] leading-relaxed" dangerouslySetInnerHTML={{ __html: doc.content || "<p><em>(empty document)</em></p>" }} />
              </div>
              <form onSubmit={submit} className="lg:col-span-2 border border-[#E8E3F0] bg-white p-6 space-y-4 self-start" data-testid="public-sign-form">
                <div>
                  <div className="label-eyebrow mb-2">Your full name</div>
                  <input
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-none border border-[#E8E3F0] px-3 py-2 text-sm focus:outline-none focus:border-[#2A0F5A]"
                    data-testid="public-name-input"
                  />
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Email (optional)</div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-none border border-[#E8E3F0] px-3 py-2 text-sm focus:outline-none focus:border-[#2A0F5A]"
                    data-testid="public-email-input"
                  />
                </div>
                <div>
                  <div className="label-eyebrow mb-2">Signature</div>
                  <SignaturePad value={signature} onChange={setSignature} testId="public-signature-pad" />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
                  data-testid="public-sign-submit-btn"
                >
                  {submitting ? "Submitting…" : "Sign document"}
                </Button>
                <p className="text-[11px] text-[#4A5568] leading-relaxed">By signing, you confirm that you have read this document and agree to be bound by its terms. Your IP address and timestamp will be recorded for the audit trail.</p>
              </form>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-[#E8E3F0] bg-white mt-16">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-[#4A5568] flex flex-wrap items-center justify-between gap-4">
          <div>© 2026 Marianne by Mahakali Tribunal. Educational assistance — not legal advice.</div>
          <div>Powered by <span className="text-[#7B56C9] font-medium">Marianne Sign &amp; Send</span></div>
        </div>
      </footer>
    </div>
  );
}
